import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Badge,
  Row,
  Col,
  InputGroup
} from 'react-bootstrap';
import { FaTrashAlt, FaInfoCircle, FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate } from "react-router-dom";
import { driversCompanyAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import '../../assets/css/Driver.css';
import Swal from 'sweetalert2';
import { useSearchFilter } from '../useSearchFilter';

// Constants for better maintainability
const TABLE_COLUMNS = [
  { key: 'index', label: '#' },
  { key: 'firstname', label: 'first_name' },
  { key: 'lastname', label: 'last_name' },
  { key: 'email', label: 'email' },
  { key: 'telphone', label: 'phone' },
  { key: 'rating', label: 'rating' },
  { key: 'status', label: 'status' },
  { key: 'approved', label: 'approved' },
  { key: 'action', label: 'action' }
];

const RATING_STARS = [1, 2, 3, 4, 5];

function DriverCompany() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({
    data: [],
    loading: true,
    error: '',
    showDelete: false,
    deleteId: null,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0,
    }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(state.data, searchTerm, ['email']);

  const location = useLocation();
  
  const highlightedUserId = location.state?.scrollToUser;

  useEffect(() => {
    if (location.state?.fromPage) {
      fetchDrivers(location.state.fromPage);
    }
  }, [location.state]);

  // Use useCallback to prevent unnecessary re-renders
  const fetchDrivers = useCallback(async (page = 1) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: '' }));
      
      const response = await driversCompanyAPI.index({
        page,
        per_page: state.pagination.per_page,
      });

      if (response.success) {
        setState(prev => ({
          ...prev,
          data: response.data.data || [],
          pagination: response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
          error: ''
        }));
      } else {
        setState(prev => ({
          ...prev,
          error: response.message || t('failed_to_fetch_data')
        }));
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: t('failed_to_fetch_data')
      }));
      console.error('Error fetching drivers:', err);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [state.pagination.per_page, t]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    if (!location.state?.fromPage) {
      fetchDrivers();
    }
  }, [fetchDrivers]);

  useEffect(() => {
    if (highlightedUserId) {
      const element = document.getElementById(`user-${highlightedUserId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [state]);

  const handleDeleteConfirmation = (id) => {
    setState(prev => ({
      ...prev,
      deleteId: id,
      showDelete: true
    }));
  };

  const handleDeleteCancel = () => {
    setState(prev => ({
      ...prev,
      showDelete: false,
      deleteId: null
    }));
  };

  const confirmDelete = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const response = await driversCompanyAPI.delete(state.deleteId);
      
      if (response.success) {
        handleDeleteCancel();
        fetchDrivers(state.pagination.current_page);
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('driver_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        setState(prev => ({
          ...prev,
          error: response.message || t('failed_to_delete_driver')
        }));
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: t('failed_to_delete_driver')
      }));
      console.error("Error deleting driver:", err);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await driversCompanyAPI.toggleStatus(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('user_status_updated_successfully'),
          timer: 2000,
          showConfirmButton: false,
        });
        fetchDrivers(state.pagination.current_page);
      } else {
        setState(prev => ({
          ...prev,
          error: response.message || t('failed_to_toggle_status')
        }));
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: t('failed_to_toggle_status')
      }));
      console.error("Error toggling driver status:", err);
    }
  };

  const handleViewDetails = (driverId) => {
    navigate(`/admin/drivers-company/details/${driverId}`, { 
      state: { fromPage: state.pagination.current_page, userId: driverId } 
    });
  };

  const handleAddNew = () => {
    navigate("/admin/drivers-company/new");
  };

  // Helper function to render rating stars
  const renderRatingStars = (rating) => {
    return RATING_STARS.map((star) => (
      <span
        key={star}
        className={`fs-4 ${star <= Math.round(rating || 0) ? 'text-warning' : 'text-muted'}`}
      >
        ★
      </span>
    ));
  };

  // Helper function to get row number
  const getRowNumber = (index) => {
    return (state.pagination.current_page - 1) * state.pagination.per_page + index + 1;
  };

  if (state.loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
          <h3 className="fw-bold mb-0">👤 {t('drivers_list')}</h3>
          <Button 
            className="add-btn btn btn-primary" 
            onClick={handleAddNew}
            disabled={state.loading}
          >
            ➕ {t('Neu_hinzufügen')}
          </Button>
        </div>

        {/* Search Bar */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        {state.error && (
          <Alert variant="danger" dismissible onClose={() => setState(prev => ({ ...prev, error: '' }))}>
            {state.error}
          </Alert>
        )}

        <div className="table-container shadow-sm rounded">
          <div className='table-responsive'>
            <Table bordered hover className="custom-table mb-0 text-center">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>{t('first_name')}</th>
                  <th>{t('company_name')}</th>
                  <th>{t('last_name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('rating')}</th>
                  <th>{t('status')}</th>
                  <th>{t('approved')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((driver, index) => (
                    <tr key={driver.id} id={`user-${driver.id}`} className={driver.id === highlightedUserId ? "user-highlight" : ""}>
                      <td>{getRowNumber(index)}</td>
                      <td>{driver.firstname || '-'}</td>
                      <td>{driver.company_name || '-'}</td>
                      <td>{driver.lastname || '-'}</td>
                      <td>
                        {searchTerm ? (
                          <span 
                            dangerouslySetInnerHTML={{
                              __html: driver.email?.replace(
                                new RegExp(searchTerm, 'gi'), 
                                match => `<mark class="bg-warning">${match}</mark>`
                              )
                            }} 
                          />
                        ) : (
                          driver.email
                        )}
                      </td>
                      <td>{driver.telphone || '-'}</td>
                      <td>{renderRatingStars(driver.average_rating)}</td>
                      <td>
                        <Badge bg={driver.status === 'Approved' ? 'success' : 'danger'}>
                          {driver.status === 'Approved' ? t('active') : t('inactive')}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <Form.Check
                            type="switch"
                            id={`approved-${driver.id}`}
                            checked={driver.admin_approve === "2" || driver.admin_approve === 2}
                            onChange={() => toggleStatus(driver.id)}
                            className="custom-switch"
                          />
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="outline-info"
                          className="me-2"
                          onClick={() => handleViewDetails(driver.id)}
                          title={t('view_details')}
                          size="sm"
                        >
                          <FaInfoCircle />
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleDeleteConfirmation(driver.id)}
                          title={t('Delete')}
                          size="sm"
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length} className="text-center py-4">
                      {t('no_drivers_found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {state.pagination.total > 0 && (
          <PaginationComponent
            currentPage={state.pagination.current_page}
            lastPage={state.pagination.last_page}
            onPageChange={fetchDrivers}
            disabled={state.loading}
          />
        )}

        {/* Delete Modal */}
        <Modal show={state.showDelete} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t('confirm_delete_driver')}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={state.loading}>
              {state.loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .postal-codes-container {
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          background-color: #f8f9fa;
        }
        
        mark {
          background-color: #ffc107;
          padding: 0.1rem 0.2rem;
          border-radius: 0.2rem;
        }
        
        .user-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
        }
        
        .user-highlight td {
          font-weight: 600;
          color: #028d22ff;
        }
      `}</style>
    </>
  );
}

export default DriverCompany;